import { css } from "dx-styles";
import type { ReactNode } from "react";

import { tokens } from "../theme/tokens";

/**
 * The reading layout for the privacy and terms pages: one column, narrower than
 * the app's, because these are the only pages here anyone reads top to bottom.
 */
const page = css({
  maxWidth: "720px",
  margin: "0 auto",
  padding: tokens.space.lg,
  display: "flex",
  flexDirection: "column",
  gap: tokens.space.md,
});

const title = css({ fontSize: "26px", fontWeight: 700, margin: 0 });

const sectionHeading = css({
  fontSize: "17px",
  fontWeight: 700,
  margin: `${tokens.space.lg} 0 0`,
});

const body = css({
  margin: 0,
  "& + &": { marginBlockStart: tokens.space.sm },
});

const list = css({
  margin: 0,
  paddingInlineStart: tokens.space.lg,
  display: "flex",
  flexDirection: "column",
  gap: tokens.space.sm,
});

const records = css({ display: "flex", flexDirection: "column", gap: tokens.space.md });

const record = css({
  borderTop: `1px solid ${tokens.color.rule}`,
  paddingBlockStart: tokens.space.md,
  display: "flex",
  flexDirection: "column",
  gap: tokens.space.xs,
});

const recordLine = css({
  display: "grid",
  // Wide enough for the labels, and stacked once the value has less than a line
  // of room — three columns of prose at phone width was unreadable.
  gridTemplateColumns: "72px 1fr",
  gap: tokens.space.sm,
  color: tokens.color.pencil,
  fontSize: "14px",
  "@media (max-width: 420px)": { gridTemplateColumns: "1fr", gap: 0 },
});

const recordLabel = css({
  fontSize: "12px",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: tokens.color.pencil,
});

const table = css({
  width: "100%",
  borderCollapse: "collapse",
  "& th, & td": {
    textAlign: "start",
    verticalAlign: "top",
    padding: `${tokens.space.sm} ${tokens.space.sm} ${tokens.space.sm} 0`,
    borderBottom: `1px solid ${tokens.color.rule}`,
  },
  "& th": { fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.06em", color: tokens.color.pencil },
});

// Wide tables scroll inside themselves rather than pushing the page sideways.
const tableScroll = css({ overflowX: "auto" });

const footnote = css({ color: tokens.color.pencil, fontSize: "13px" });

export function Prose({ children }: { readonly children: ReactNode }) {
  return <main className={page}>{children}</main>;
}

export function ProseTitle({ children }: { readonly children: ReactNode }) {
  return <h1 className={title}>{children}</h1>;
}

export function ProseHeading({ children }: { readonly children: ReactNode }) {
  return <h2 className={sectionHeading}>{children}</h2>;
}

export function P({ children }: { readonly children: ReactNode }) {
  return <p className={body}>{children}</p>;
}

export function Bullets({ children }: { readonly children: ReactNode }) {
  return <ul className={list}>{children}</ul>;
}

/**
 * The "what is stored" list. A three-column table put three columns of prose
 * next to each other, which is unreadable on a phone and not much better on a
 * desktop; each entry is its own block instead.
 */
export function Records({ children }: { readonly children: ReactNode }) {
  return <div className={records}>{children}</div>;
}

export function Record({
  what,
  why,
  kept,
}: {
  readonly what: ReactNode;
  readonly why: ReactNode;
  readonly kept: ReactNode;
}) {
  return (
    <div className={record}>
      <div>{what}</div>
      <div className={recordLine}>
        <span className={recordLabel}>Why</span>
        <span>{why}</span>
      </div>
      <div className={recordLine}>
        <span className={recordLabel}>Kept</span>
        <span>{kept}</span>
      </div>
    </div>
  );
}

export function ProseTable({ children }: { readonly children: ReactNode }) {
  return (
    <div className={tableScroll}>
      <table className={table}>{children}</table>
    </div>
  );
}

export function Footnote({ children }: { readonly children: ReactNode }) {
  return <p className={footnote}>{children}</p>;
}
